import { notification } from "antd";
import cuid from "cuid";
import { defaults, defaultsDeep, find, get, keyBy, map } from "lodash";
import moment from "moment";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "shared/components/Header";
import ProcessStepper from "shared/components/ProcessStepper";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { CONTACT_POINT_SYSTEM } from "utils/constants";
import View from "./View";

const steps = [
  { title: "Qualification" },
  { title: "Sélection<br />produits" },
  { title: "Devis" },
  { title: "Souscription" },
  { title: "Documents" },
  { title: "Signature<br />En attente" },
  { title: "Souscription terminée" },
];

const getRecommendation = (membersCount) => {
  if (membersCount === 0)
    return {
      formula: "Solo",
      text: "Le prospect souhaite une couverture individuelle",
    };
  if (membersCount === 1)
    return { formula: "Duo", text: "Couverture pour 2 personnes du foyer" };
  return {
    formula: "Famille",
    text: `Couverture pour ${membersCount + 1} personnes du foyer`,
  };
};
const Contact = ({
  project,
  onNext,
  setContactOpen,
  setContactId,
  setDrawerProject,
  currentStatus,
}) => {
  const [nextLoading, setNextLoading] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const [updateProject] = useMutation(graphql.mutations.UPDATE_PROJECT, {
    refetchQueries: [
      {
        query: graphql.queries.PROJECT,
        awaitRefetchQueries: true,
        variables: { where: { id: project.id } },
      },
    ],
  });
  const [checkBlackList] = useMutation(graphql.mutations.CHECK_BLACK_LIST);
  const TRANSLATION = t("DETAIL.TEMPLATES.LAFOURGALE.CONTACT", {
    returnObjects: true,
  });

  const handleDuplicateClick = (drawerProject) => {
    setContactId(get(drawerProject, "contact.id"));
    setDrawerProject(drawerProject);
    setContactOpen(true);
  };

  const initialValues = useMemo(
    () =>
      defaultsDeep(
        {
          contact: {
            ...project.contact,
            fields: {
              ...get(project, "contact.fields", {}),
              mobileMeans: get(project, "contact.fields.mobileMeans") || [],
            },
            telecoms: defaults(
              keyBy(get(project, "contact.telecoms"), "system"),
              {
                [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
              },
            ),
          },
          members: (get(project, "contact.relatedPersons", []) || []).map(
            (p) => ({
              id: p.id || cuid(),
              firstName: get(p, "identity.user.firstname", ""),
              lastName: get(p, "identity.user.lastname", ""),
              birthDate: get(p, "identity.user.birthDate", null),
              relationship: get(p, "relationship", ""),
            }),
          ),
          zipCode: find(get(project, "contact.telecoms", []), {
            system: CONTACT_POINT_SYSTEM.ZIP_CODE,
          }),
          phoneNumber: find(get(project, "contact.telecoms", []), {
            system: CONTACT_POINT_SYSTEM.PHONE,
          }),
        },
        {
          members: [],
          contact: {
            id: cuid(),
            user: {
              id: cuid(),
              firstname: null,
              lastname: null,
              birthDate: null,
            },
            telecoms: {
              [CONTACT_POINT_SYSTEM.ZIP_CODE]: { id: cuid(), value: null },
              [CONTACT_POINT_SYSTEM.PHONE]: { id: cuid(), value: null },
              [CONTACT_POINT_SYSTEM.EMAIL]: { id: cuid(), value: null },
            },
          },
        },
      ),
    [],
  );

  const onSubmit = async (values) => {
    setNextLoading(true);
    const { members, contact } = values;
    const relatedPersons = members.map((m) => ({
      id: m.id,
      relationship: m.relationship,
      identity: {
        id: cuid(),
        user: {
          id: cuid(),
          firstname: m.firstName,
          lastname: m.lastName,
          birthDate: m.birthDate || null,
        },
      },
    }));

    const recommendation = getRecommendation(members.length);
    const mappedTelecoms = map(get(values, "contact.telecoms"), (item, key) =>
      defaults(item, { id: cuid(), system: key }),
    );

    const issuanceDate =
      get(project, "contract.issuanceDate") || moment().endOf("day");

    await updateProject({
      variables: {
        where: { id: project.id },
        data: {
          contact: {
            ...contact,
            user: contact.user,
            relatedPersons,
            telecoms: mappedTelecoms,
          },
        },
      },
    });

    checkBlackList({
      variables: {
        where: { id: project.id },
      },
      onCompleted: ({ checkBlackList: { status } }) => {
        setNextLoading(false);
        if (status)
          return notification.open({
            message: "Client sur liste noire",
            duration: 5,
            type: "warning",
          });
        onNext({
          payload: {
            contract: {
              id: cuid(),
              ...get(project, "contract", {}),
              issuanceDate,
            },
            visible: true,
            fields: {
              ...project.fields,
              recommendedFormula: recommendation.formula,
              householdSize: members.length + 1,
            },
          },
          onCompleted: () => setNextLoading(false),
        });
      },
      onError: (error) => {
        console.log("error >>>", error);
      },
    });
  };

  return (
    <>
      <Header key="header" title={TRANSLATION.TITLE} actions={[]} />
      <ProcessStepper steps={steps} current={+get(currentStatus, "order")} />
      <View
        onSubmit={onSubmit}
        initialValues={initialValues}
        nextLoading={nextLoading}
        getRecommendation={getRecommendation}
        onDuplicateClick={handleDuplicateClick}
      />
    </>
  );
};

export default Contact;
